import React, { useState, useEffect } from "react";
import {
  Search, Eye, X, AlertCircle, RefreshCw, Wifi, WifiOff,
  Wrench, Activity, Building2, Smartphone, Clock
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import {
  listAdminConnections,
  showAdminConnection,
  diagnoseConnection,
  restartConnectionSocket,
  repairConnection,
  listAllCompanies
} from "../../services/adminConnections";

const STATUS_MAP = {
  CONNECTED: { label: "Conectado", color: "ativo" },
  DISCONNECTED: { label: "Desconectado", color: "inactive" },
  OPENING: { label: "Conectando...", color: "pendente" },
  qrcode: { label: "QR Code", color: "pendente" },
  TIMEOUT: { label: "Timeout", color: "inativo" },
  PAIRING: { label: "Pareando", color: "pendente" }
};

const CHANNEL_ICONS = { whatsapp: "WhatsApp", facebook: "Facebook", instagram: "Instagram", whatsapp_oficial: "Oficial" };

const Connections = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", companyId: "", status: "", channel: "" });
  const [toast, setToast] = useState(null);
  const [detailModal, setDetailModal] = useState({ open: false, item: null, loading: false });
  const [diagModal, setDiagModal] = useState({ open: false, item: null, result: null, loading: false });

  useEffect(() => { load(1); fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const data = await listAllCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch { /* optional */ }
  };

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const query = { pageNumber: page, pageSize: 20, sortBy: "updatedAt", sortOrder: "DESC" };
      if (filters.search) query.search = filters.search;
      if (filters.companyId) query.companyId = filters.companyId;
      if (filters.status) query.status = filters.status;
      if (filters.channel) query.channel = filters.channel;
      const data = await listAdminConnections(query);
      setItems(data.items || []);
      setPagination(data.pagination || {});
    } catch (err) {
      setError(err.message || "Erro ao carregar conexões");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => { e.preventDefault(); load(1); };
  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => { setFilters({ search: "", companyId: "", status: "", channel: "" }); setTimeout(() => load(1), 0); };

  const openDetail = async (row) => {
    setDetailModal({ open: true, item: row, loading: true });
    try {
      const data = await showAdminConnection(row.id);
      setDetailModal({ open: true, item: data, loading: false });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Erro ao buscar detalhes" });
      setDetailModal({ open: false, item: null, loading: false });
    }
  };

  const openDiag = async (row) => {
    setDiagModal({ open: true, item: row, result: null, loading: true });
    try {
      const result = await diagnoseConnection(row.id);
      setDiagModal({ open: true, item: row, result, loading: false });
    } catch (err) {
      setDiagModal({ open: true, item: row, result: { classification: "error", reason: err.message }, loading: false });
    }
  };

  const handleRestart = async (row) => {
    try {
      await restartConnectionSocket(row.id);
      setToast({ type: "success", message: "Socket reiniciado" });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao reiniciar" }); }
  };

  const handleRepair = async (row) => {
    try {
      await repairConnection(row.id);
      setToast({ type: "success", message: "Reparo executado" });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao reparar" }); }
  };

  const columns = [
    { header: "Empresa", render: (row) => <span className="font-semibold text-gray-900 dark:text-gray-100">{row.companyName || `#${row.companyId}`}</span> },
    { header: "Conexão", render: (row) => <span className="font-bold text-purple-600 dark:text-purple-400">{row.name}</span> },
    { header: "Canal", render: (row) => <span className="text-xs">{CHANNEL_ICONS[row.channel] || row.channel}</span> },
    { header: "Número", render: (row) => <span className="font-mono text-xs">{row.number || "—"}</span> },
    {
      header: "Status", render: (row) => {
        const s = STATUS_MAP[row.status] || { label: row.status, color: "default" };
        return <StatusBadge status={s.color} label={s.label} />;
      }
    },
    { header: "Atualizado", render: (row) => <span className="text-xs text-gray-500">{new Date(row.updatedAt).toLocaleString("pt-BR")}</span> },
    {
      header: "Ações", render: (row) => (
        <div className="flex items-center justify-end gap-1.5">
          <button onClick={() => openDetail(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors" title="Detalhes">
            <Eye size={15} />
          </button>
          <button onClick={() => openDiag(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 transition-colors" title="Diagnosticar">
            <Activity size={15} />
          </button>
          <button onClick={() => handleRepair(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 transition-colors" title="Reparar">
            <Wrench size={15} />
          </button>
          <button onClick={() => handleRestart(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 transition-colors" title="Reiniciar Socket">
            <RefreshCw size={15} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Operação"
        title="Gerenciar Conexões"
        subtitle={`${pagination.total || 0} conexão(ões) na plataforma`}
      />

      {error && (
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${
          toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      <form onSubmit={handleSearchSubmit} className="flex gap-2.5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-3 text-gray-400 dark:text-gray-500" />
          <input type="text" value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Buscar por nome, número ou empresa..." className="w-full pl-9 pr-4 py-2 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-purple-500" />
        </div>
        <button type="submit" className="px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">Filtrar</button>
        <button type="button" onClick={clearFilters} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl">Limpar</button>
      </form>

      <div className="flex flex-wrap gap-2">
        <select value={filters.companyId} onChange={(e) => handleFilterChange("companyId", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todas as empresas</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todos os status</option>
          <option value="CONNECTED">Conectado</option>
          <option value="DISCONNECTED">Desconectado</option>
          <option value="OPENING">Conectando</option>
          <option value="qrcode">QR Code</option>
          <option value="TIMEOUT">Timeout</option>
        </select>
        <select value={filters.channel} onChange={(e) => handleFilterChange("channel", e.target.value)}
          className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todos os canais</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="whatsapp_oficial">WhatsApp Oficial</option>
        </select>
      </div>

      <DataTable columns={columns} data={items} loading={loading} emptyTitle="Nenhuma conexão encontrada" emptyDescription="Tente buscar por outro termo ou limpe os filtros." />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
          <span>Página {pagination.pageNumber} de {pagination.totalPages} ({pagination.total} conexões)</span>
          <div className="flex gap-2">
            <button disabled={pagination.pageNumber <= 1} onClick={() => load(pagination.pageNumber - 1)} className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10">Anterior</button>
            <button disabled={pagination.pageNumber >= pagination.totalPages} onClick={() => load(pagination.pageNumber + 1)} className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10">Próxima</button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModal.open && detailModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-lg p-6 border border-gray-200 dark:border-white/10 relative space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Detalhes da Conexão</h3>
              <button onClick={() => setDetailModal({ open: false, item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            {detailModal.loading ? (
              <div className="py-6 text-center text-xs text-gray-400">Carregando...</div>
            ) : (
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-gray-500">Empresa:</span> <span className="font-semibold">{detailModal.item.companyName || `#${detailModal.item.companyId}`}</span></div>
                  <div><span className="text-gray-500">Company ID:</span> <span className="font-mono">{detailModal.item.companyId}</span></div>
                  <div><span className="text-gray-500">Nome:</span> <span className="font-semibold">{detailModal.item.name}</span></div>
                  <div><span className="text-gray-500">ID:</span> <span className="font-mono">{detailModal.item.id}</span></div>
                  <div><span className="text-gray-500">Número:</span> <span className="font-mono">{detailModal.item.number || "—"}</span></div>
                  <div><span className="text-gray-500">Canal:</span> <span>{CHANNEL_ICONS[detailModal.item.channel] || detailModal.item.channel}</span></div>
                  <div><span className="text-gray-500">Status:</span> <StatusBadge status={(STATUS_MAP[detailModal.item.status] || {}).color || "default"} label={(STATUS_MAP[detailModal.item.status] || {}).label || detailModal.item.status} /></div>
                  <div><span className="text-gray-500">Padrão:</span> <span>{detailModal.item.isDefault ? "Sim" : "Não"}</span></div>
                </div>
                {detailModal.item.createdAt && <div><span className="text-gray-500">Criado em:</span> {new Date(detailModal.item.createdAt).toLocaleString("pt-BR")}</div>}
                {detailModal.item.updatedAt && <div><span className="text-gray-500">Atualizado:</span> {new Date(detailModal.item.updatedAt).toLocaleString("pt-BR")}</div>}
                {detailModal.item.queues?.length > 0 && (
                  <div>
                    <span className="text-gray-500">Filas:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detailModal.item.queues.map(q => (
                        <span key={q.id} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: q.color + "20", color: q.color }}>{q.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Diagnose Modal */}
      {diagModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-md p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Diagnóstico — {diagModal.item?.name}</h3>
              <button onClick={() => setDiagModal({ open: false, item: null, result: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            {diagModal.loading ? (
              <div className="py-6 text-center text-xs text-gray-400">Diagnosticando...</div>
            ) : diagModal.result ? (
              <div className="space-y-3 text-xs">
                <div className="rounded-xl bg-gray-50 dark:bg-[#191c1e] p-4 space-y-2">
                  <div><span className="text-gray-500">Classificação:</span> <span className="font-semibold">{diagModal.result.classification || "—"}</span></div>
                  <div><span className="text-gray-500">Motivo:</span> <span>{diagModal.result.reason || "—"}</span></div>
                  <div><span className="text-gray-500">Recomendação:</span> <span>{diagModal.result.recommendation || "—"}</span></div>
                </div>
              </div>
            ) : (
              <div className="py-6 text-center text-xs text-gray-400">Sem dados de diagnóstico</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Connections;
